// Kit 01 · items 01 to 07, 10, 12.
//
//   01 Button, primary      02 Button, secondary   03 Button, ghost
//   04 Button, destructive  05 Button, icon only   06 Button with icon
//   07 Loading, the rule    10 Button group        12 Copy to clipboard
//
// redline: height 36 · padding 0 16 (ghost 0 12) · radius 8 · gap 8
//          label 14/600 · icon 16 leading · icon-only 28 square, radius 6
// keys:    Enter or Space activates. Tab moves focus. No access key.

import { forwardRef, useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Check, Copy, LoaderCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "./icon";
import { cx, isInert, stateAttr } from "./kit-state";
import type { Stateful } from "./kit-state";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "k-btn--pri",
  secondary: "k-btn--sec",
  ghost: "k-btn--ghost",
  destructive: "k-btn--des",
};

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">, Stateful {
  variant?: ButtonVariant;
  children: ReactNode;
  /** Item 06. Leading only, never both sides — the redline is explicit. */
  icon?: LucideIcon;
  /** Item 07. The label shown while busy. */
  loadingLabel?: string;
  loading?: boolean;
}

/**
 * Item 07 is the reason this component is more than a className.
 *
 * A button is content sized, so swapping "Start analysis" for "Measuring"
 * resizes it and everything beside it moves. The specimen reserves a fixed
 * width at the call site, chosen to fit both labels.
 *
 * This derives that width instead. BOTH labels are always in the box, stacked
 * in one grid cell, with the inactive one hidden — so the button measures
 * max(rest, loading) in both states and the two are equal by construction.
 *
 * The first attempt only held the rest width, and the gate caught it: with a
 * spinner beside it, "Measuring" was 94.27 against "Start analysis" at 92.16,
 * so loading was WIDER and the row still moved. Reserving the larger of the
 * two is the only version where the claim "nothing beside it moves" is true.
 *
 * It forwards a ref because Kit 03 item 56 needs one: "Focus opens on the
 * cancel control for destructive actions" means the caller has to be able to
 * name WHICH button, and a component that cannot be pointed at cannot be the
 * one focus opens on.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = "primary",
  children,
  icon,
  loading = false,
  loadingLabel,
  state,
  className,
  disabled,
  ...rest
}: ButtonProps, ref) {
  const busy = loading || state === "loading";
  const label = busy && loadingLabel ? loadingLabel : children;

  return (
    <button
      ref={ref}
      type="button"
      className={cx("k-btn", VARIANT_CLASS[variant], className)}
      disabled={disabled || isInert(state)}
      aria-busy={busy || undefined}
      {...stateAttr(state)}
      {...rest}
    >
      <span className="k-btn__hold">
        {/* The reservation. Both labels are in the box whenever the label can
            change, so the grid cell is max(rest, loading) in either state and
            the two measure the same. Only rendered when there IS a second
            label: a spinner beside an unchanged label does not resize
            anything, and an empty reservation would just be a wider button. */}
        {loadingLabel ? (
          <span className="k-btn__ghost-label" aria-hidden="true">
            <span className="k-btn__live">
              {busy ? (
                <>
                  {icon ? <Icon icon={icon} /> : null}
                  {children}
                </>
              ) : (
                <>
                  <span className="k-spin"><Icon icon={LoaderCircle} size="sm" /></span>
                  {loadingLabel}
                </>
              )}
            </span>
          </span>
        ) : null}
        <span className="k-btn__live">
          {busy ? (
            <span className="k-spin">
              <Icon icon={LoaderCircle} size="sm" />
            </span>
          ) : icon ? (
            <Icon icon={icon} />
          ) : null}
          {label}
        </span>
      </span>
    </button>
  );
});

/* ── 05 · icon only ─────────────────────────────────────────────────────────
   redline: 28 square · radius 6 · icon 14 · hit area 44 on touch
   An icon with no adjacent label carries the whole meaning, so the label is
   required rather than optional. F10: "An icon without an adjacent label must
   be a universally understood glyph or it does not ship." */

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">, Stateful {
  icon: LucideIcon;
  /** Required. It is the accessible name and the tooltip. */
  label: string;
}

export function IconButton({ icon, label, state, className, disabled, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      className={cx("k-icon-btn", className)}
      aria-label={label}
      title={label}
      disabled={disabled || isInert(state)}
      {...stateAttr(state)}
      {...rest}
    >
      <Icon icon={icon} size="sm" />
    </button>
  );
}

/* ── 12 · copy to clipboard ─────────────────────────────────────────────────
   redline: 28 square · icon 14 · confirm swaps glyph for 1200ms
   The hold is --d-confirm, read out of the stylesheet rather than retyped, so
   the glyph reverts on the same beat the box cools. */

export interface CopyButtonProps extends Stateful {
  value: string;
  /** Default: the canonical "Copy". */
  label?: string;
  confirmedLabel?: string;
  className?: string;
  disabled?: boolean;
}

export function CopyButton({
  value,
  label = "Copy",
  confirmedLabel = "Copied",
  state,
  className,
  disabled,
}: CopyButtonProps) {
  const [confirmed, setConfirmed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const node = useRef<HTMLButtonElement | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // The forced state drives the glyph too, so the gallery shows the confirmed
  // box and the confirmed glyph together rather than one without the other.
  const showConfirmed = confirmed || state === "active";

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // A denied clipboard permission is not an error the user can act on and
      // there is nothing to say about it. The confirmation simply does not fire.
      return;
    }
    setConfirmed(true);
    const hold = node.current
      ? getComputedStyle(node.current).getPropertyValue("--d-confirm").trim()
      : "";
    const ms = Number.parseFloat(hold) || 0;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setConfirmed(false), ms);
  }

  return (
    <button
      ref={node}
      type="button"
      className={cx("k-icon-btn", className)}
      aria-label={showConfirmed ? confirmedLabel : label}
      title={showConfirmed ? confirmedLabel : label}
      data-confirmed={showConfirmed || undefined}
      aria-busy={state === "loading" || undefined}
      disabled={disabled || isInert(state)}
      onClick={copy}
      {...stateAttr(state)}
    >
      {state === "loading" ? (
        <span className="k-spin"><Icon icon={LoaderCircle} size="sm" /></span>
      ) : (
        <Icon icon={showConfirmed ? Check : Copy} size="sm" />
      )}
    </button>
  );
}

/* ── 10 · button group ──────────────────────────────────────────────────────
   redline: segments abut with a shared 1 border · radius 8 on outer corners
   keys:    Each button is its own tab stop.
   Peer actions: nothing is selected, so there is no selected state. Three is
   the maximum before it becomes a menu — which is why this takes children and
   not an items array, so the count is visible at the call site. */

export function ButtonGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx("k-btn-group", className)} role="group">
      {children}
    </div>
  );
}
